// src/context/AuthContext.tsx

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { disconnectSignalR } from "../api/signalRClient";

// ============================================================
// USER COMPANY
// ============================================================

export interface UserCompany {
  companyNo: number;
  companyName: string;
  userType?: string | null;
}

// ============================================================
// AUTH USER
//
// Kept for reference / future typing.
// Existing application continues using `any` in the
// AuthContext so existing LoginForm and other components
// do not get TypeScript compatibility errors.
// ============================================================

export interface AuthUser {
  userNo: number;
  userName: string;
  fullName?: string;
  email?: string;
  phoneNo?: string;
  companies?: UserCompany[];
}

// ============================================================
// AUTH CONTEXT TYPE
// ============================================================

type AuthContextType = {
  token: string | null;
  user: any | null;
  isAuthenticated: boolean;
  initialized: boolean;

  setAuth: (
    token: string | null,
    user?: any | null
  ) => void;

  logout: () => void;
};

// ============================================================
// CONTEXT
// ============================================================

const AuthContext = createContext<
  AuthContextType | undefined
>(undefined);

// ============================================================
// PROVIDER
// ============================================================

export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {

  const [token, setToken] =
    useState<string | null>(null);

  const [user, setUser] =
    useState<any | null>(null);

  const [initialized, setInitialized] =
    useState(false);

  // ==========================================================
  // AUTHENTICATION
  // ==========================================================

  const isAuthenticated = Boolean(user);

  // ==========================================================
  // RESTORE USER FROM LOCAL STORAGE
  // ==========================================================

  useEffect(() => {

    const u = localStorage.getItem("user");

    if (u) {
      try {
        setUser(JSON.parse(u));
      } catch {
        localStorage.removeItem("user");
      }
    }

    setInitialized(true);

  }, []);

  // ==========================================================
  // KEEP LOCAL STORAGE IN SYNC
  // ==========================================================

  useEffect(() => {

    if (!initialized) return;

    if (user) {

      localStorage.setItem(
        "user",
        JSON.stringify(user)
      );

    } else {

      localStorage.removeItem("user");

    }

  }, [user, initialized]);

  // ==========================================================
  // SET AUTH
  // ==========================================================

  const setAuth = (
    t: string | null,
    u: any | null = null
  ) => {

    setToken(t ?? null);

    setUser(u);

    if (u) {

      localStorage.setItem(
        "user",
        JSON.stringify(u)
      );

    } else {

      localStorage.removeItem("user");

    }
  };

  // ==========================================================
  // LOGOUT
  // ==========================================================

  const logout = () => {

    setToken(null);

    setUser(null);

    localStorage.removeItem("token");
    localStorage.removeItem("user");

    disconnectSignalR();

    setAuth(null, null);
  };

  // ==========================================================
  // PROVIDER
  // ==========================================================

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        isAuthenticated,
        initialized,
        setAuth,
        logout,
      }}
    >.
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================
// USE AUTH
// ============================================================

export function useAuth() {

  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error(
      "useAuth must be used within <AuthProvider>"
    );
  }

  return ctx;
}